import { Users, Building2, Star } from "lucide-react";

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0";
  const num = parseFloat(amount);
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}k`;
  return `$${num.toFixed(0)}`;
};

const RANK_STYLES = [
  { bg: "#FEF3C7", fg: "#B45309" },
  { bg: "#EEF0F4", fg: "#2B3142" },
  { bg: "#FDE8D7", fg: "#9A5B2E" },
  { bg: "#F5F6F9", fg: "#8A90A2" },
];

const LeaderboardCard = ({ title, icon: Icon, chipBg, chipFg, valFg, items, renderItem }) => (
  <section className="sz-card" style={{ padding: "20px 22px" }}>
    <div className="mb-3.5 flex items-center gap-2.5">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: chipBg, color: chipFg }}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <h3 className="text-[15px] font-bold text-[#0C1120]" style={{ fontFamily: "Poppins,sans-serif" }}>
        {title}
      </h3>
    </div>
    {!items || items.length === 0 ? (
      <p className="py-4 text-center text-[13px] text-[#8A90A2]">No data yet</p>
    ) : (
      <div>
        {items.slice(0, 5).map((item, idx) => {
          const rs = RANK_STYLES[Math.min(idx, 3)];
          return (
            <div key={idx} className="flex items-center gap-3 border-t border-[#F1F3F7] px-1 py-[9px] first:border-t-0">
              <span
                className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[7px] text-[11px] font-bold"
                style={{ fontFamily: "Poppins,sans-serif", background: rs.bg, color: rs.fg }}
              >
                {idx + 1}
              </span>
              {renderItem(item, valFg)}
            </div>
          );
        })}
      </div>
    )}
  </section>
);

const DashboardPerformance = ({ performance, loading }) => {
  if (loading) {
    return (
      <div className="mt-[18px] grid grid-cols-1 gap-[18px] md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="sz-card h-[260px] animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-[18px] grid grid-cols-1 gap-[18px] md:grid-cols-3">
      <LeaderboardCard
        title="Top Crew"
        icon={Users}
        chipBg="#CCFBF1"
        chipFg="#0F766E"
        valFg="#0F766E"
        items={performance?.top_crew}
        renderItem={(item, valFg) => (
          <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
            <span className="truncate text-[13px] font-medium text-[#0C1120]">{item.name || item.full_name}</span>
            <span className="flex-none text-[13px] font-bold" style={{ fontFamily: "Poppins,sans-serif", color: valFg }}>
              {item.job_count || item.jobs} jobs
            </span>
          </div>
        )}
      />
      <LeaderboardCard
        title="Top Businesses"
        icon={Building2}
        chipBg="#DBEAFE"
        chipFg="#2563EB"
        valFg="#2563EB"
        items={performance?.top_businesses}
        renderItem={(item, valFg) => (
          <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
            <span className="truncate text-[13px] font-medium text-[#0C1120]">{item.name || item.business_name}</span>
            <span className="flex-none text-[13px] font-bold" style={{ fontFamily: "Poppins,sans-serif", color: valFg }}>
              {formatCurrency(item.total_spent)}
            </span>
          </div>
        )}
      />
      <LeaderboardCard
        title="Highest Rated"
        icon={Star}
        chipBg="#FEF3C7"
        chipFg="#D97706"
        valFg="#D97706"
        items={performance?.highest_rated}
        renderItem={(item, valFg) => (
          <div className="flex flex-1 items-center justify-between gap-2 min-w-0">
            <span className="truncate text-[13px] font-medium text-[#0C1120]">{item.name || item.full_name}</span>
            <span className="flex flex-none items-center gap-1 text-[13px] font-bold" style={{ fontFamily: "Poppins,sans-serif", color: valFg }}>
              <Star className="h-3 w-3 fill-current" />
              {item.avg_rating?.toFixed(2)} ({item.review_count})
            </span>
          </div>
        )}
      />
    </div>
  );
};

export default DashboardPerformance;
